import { Component, h, Element, Prop, Host, Event, EventEmitter } from '@stencil/core';
import { getClassNames, hasSlot } from '../../utils';

/**
 * @slot -  Main accordion content.
 * @slot heading - Heading slot.
 * @slot icon - Icon slot.
 * @slot tag - Tag slot.
 * @slot heading-right - Heading right slot.
 *
 * @part accordion - Main accordion container.
 * @part header - Accordion header.
 * @part content - Accordion content.
 *
 * @class
 * @name O2Accordion
 * @description Defining the O2Accordion component.
 */
@Component({
  tag: 'o2-accordion',
  styleUrl: 'accordion.scss',
  shadow: true,
})
export class O2Accordion {
  /**
   * Counter for the accordion ids.
   */
  private accordionId: string = `wc-o2-accordion-${ids}`;

  /**
   * Counter for the accordion content ids.
   */
  private contentId: string = `wc-o2-accordion-content-${ids}`;

  /**
   * Flag to indicate whether the accordion has a heading slot or not.
   */
  private hasHeadingSlot: boolean;

  /**
   * Flag to indicate whether the accordion has an icon tag or not.
   */
  private hasTagSlot: boolean;

  /**
   * Flag to indicate whether the button has an icon slot or not.
   */
  private hasIconSlot: boolean;

  /**
   * Flag to indicate whether the button has an icon slot or not.
   */
  private hasStatusTooltip: boolean;

  /**
   * Flag to indicate whether the accordion has a heading right slot or not.
   */
  private hasHeadingRightSlot: boolean;

  /**
   * Reference to the host element of the component.
   */
  @Element() private el!: HTMLO2AccordionElement;

  /**
   * Set type of accordion type.
   */
  @Prop({ reflect: true }) type?: 'compact' | 'box' = 'compact';

  /**
   *  Set main heading.
   */
  @Prop() heading?: string;

  /**
   * By default, the accordion is closed.
   */
  @Prop({ reflect: true, mutable: true }) open? = false;

  /**
   * Fired when the accordion is opened.
   */
  @Event() o2Open!: EventEmitter<any>;

  /**
   * Fired when the accordion is closed.
   */
  @Event() o2Close!: EventEmitter<any>;

  /**
   * Toggle the accordion.
   */
  private toggle = () => {
    this.open = !this.open;
    this.open ? this.o2Open.emit() : this.o2Close.emit();
  }

  connectedCallback() {
    // Increment the ids.
    ids++;

    // Check if the accordion has a slots.
    this.hasStatusTooltip = hasSlot(this.el, '[slot="status-tooltip"]');
    this.hasHeadingSlot = hasSlot(this.el, '[slot="heading"]');
    this.hasIconSlot = hasSlot(this.el, '[slot="icon"]');
    this.hasTagSlot = hasSlot(this.el, '[slot="tag"]');
    this.hasHeadingRightSlot = hasSlot(this.el, '[slot="heading-right"]');
  }

  render() {
    const {
      hasHeadingSlot,
      hasIconSlot,
      hasTagSlot,
      hasHeadingRightSlot,
      type,
      heading,
      open,
      contentId,
      accordionId
    } = this;

    // Set the class names for the accordion.
    const accordionClassNames = getClassNames(['accordion', type && `accordion--${type}`, open && 'accordion--open']);

    return (
      <Host>
        <div class={accordionClassNames} part="accordion">
          <button
            class="accordion__header"
            id={accordionId}
            aria-expanded={open.toString()}
            aria-controls={contentId}
            onClick={this.toggle}
            part="header"
            type="button"
          >
            <div class="accordion__header-left">
              {this.hasStatusTooltip ? (
                <slot name="status-tooltip"></slot>
              ): null}

              <div class="accordion__header-inner">
                {hasIconSlot ? (
                  <slot name="icon"></slot>
                ) : null}
                {hasHeadingSlot ? (
                  <slot name="heading"></slot>
                ) : (
                  <div class="accordion__heading">
                    {heading ? heading : null}
                  </div>
                )}
              </div>
              {hasTagSlot ? (
                <div class="accordion__tag">
                  <slot name="tag"></slot>
                </div>
              ) : null}
            </div>

            <div class="accordion__header-right">
              {hasHeadingRightSlot ? (
                <div class="accordion__heading-right">
                  <slot name="heading-right"></slot>
                </div>
              ) : null}
              <div class="accordion__icon">
                <o2-icon size={20} name="chevron-down" color="blue-action"></o2-icon>
              </div>
            </div>
          </button>

          <div
            class="accordion__content"
            id={contentId}
            part="content"
            aria-hidden={!open.toString()}
          >
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }
}

// Counter for the ids.
let ids = 0;
